import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { listPatients, getPatientHistory } from "@/lib/admin.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Search } from "lucide-react";
import { useState } from "react";
import { formatAppointmentLabel } from "@/lib/booking-helpers";

export const Route = createFileRoute("/_authenticated/admin/patients")({
  component: PatientsPage,
});

function PatientsPage() {
  const list = useServerFn(listPatients);
  const history = useServerFn(getPatientHistory);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);

  const { data: patients = [], isPending } = useQuery({
    queryKey: ["admin", "patients", search],
    queryFn: () => list({ data: { search } }),
  });

  const { data: hist = [], isPending: histPending } = useQuery({
    queryKey: ["admin", "patients", "history", selected],
    queryFn: () => history({ data: { email: selected! } }),
    enabled: !!selected,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Patients</h1>
        <p className="text-sm text-muted-foreground">All patients who have booked with the practice.</p>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone" className="pl-8" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">{patients.length} patients</CardTitle></CardHeader>
        <CardContent className="p-0">
          {isPending ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : patients.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No patients yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Contact</th>
                    <th className="px-4 py-3 text-left">Medical aid</th>
                    <th className="px-4 py-3 text-right">Visits</th>
                    <th className="px-4 py-3 text-right">Spent</th>
                    <th className="px-4 py-3 text-left">Last</th>
                    <th className="px-4 py-3 text-left">Next</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {patients.map((p) => (
                    <tr key={p.email} className="border-t hover:bg-muted/30">
                      <td className="px-4 py-3 font-medium">{p.name}</td>
                      <td className="px-4 py-3 text-xs"><div>{p.email}</div><div className="text-muted-foreground">{p.phone}</div></td>
                      <td className="px-4 py-3 text-xs">{p.medical_aid ? (<><div>{p.medical_aid}</div><div className="text-muted-foreground">{p.medical_aid_number}</div></>) : <span className="text-muted-foreground"></span>}</td>
                      <td className="px-4 py-3 text-right">{p.total_visits}</td>
                      <td className="px-4 py-3 text-right">R{p.total_spent.toLocaleString("en-ZA")}</td>
                      <td className="px-4 py-3 text-xs">{p.last_appointment ? formatAppointmentLabel(p.last_appointment) : ""}</td>
                      <td className="px-4 py-3 text-xs">{p.next_appointment ? formatAppointmentLabel(p.next_appointment) : ""}</td>
                      <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => setSelected(p.email)}>View</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Patient history</DialogTitle>
            <DialogDescription>{selected}</DialogDescription>
          </DialogHeader>
          {histPending ? (
            <div className="flex h-32 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              <ul className="divide-y">
                {hist.map((b: any) => (
                  <li key={b.id} className="py-3 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium">{b.service}</span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{b.status}</span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">{formatAppointmentLabel(b.appointment_at)} · {b.location} · {b.price}</div>
                    <div className="mt-1 text-xs">{b.condition}</div>
                    {b.private_notes && <div className="mt-1 rounded bg-muted p-2 text-xs italic">{b.private_notes}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
